import React, { useEffect } from 'react';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'raviger';
import { system } from 'api/client';

export const Auth0ProviderWithHistory = ({ children }) => {
    const navigate = useNavigate();

    const domain = process.env[`REACT_APP_AUTH0_DOMAIN_${system.toUpperCase()}`];
    const clientId = process.env[`REACT_APP_AUTH0_CLIENT_ID_${system.toUpperCase()}`];
    const audience = process.env[`REACT_APP_AUTH0_AUDIENCE_${system.toUpperCase()}`];
    const redirectUri = window.location.origin;

    const onRedirectCallback = (appState) => {
        console.log('🔄 Redirect callback triggered', appState);
        // Don't navigate here - let Auth0RedirectHandler do it after full authentication
    };

    if (!domain || !clientId) {
        console.error('❌ Auth0 configuration missing!');
        return null;
    }

    console.log('🔧 Auth0Provider initialized');

    return (
        <Auth0Provider
            domain={domain}
            clientId={clientId}
            authorizationParams={{
                redirect_uri: redirectUri,
                audience: audience,
                scope: "openid profile email offline_access"
            }}
            onRedirectCallback={onRedirectCallback}
            useRefreshTokens={true}
            useRefreshTokensFallback={false}
            cacheLocation="localstorage"
        >
            <Auth0RedirectHandler>
                {children}
            </Auth0RedirectHandler>
        </Auth0Provider>
    );
};

// Component to handle post-redirect authentication with Feathers
const Auth0RedirectHandler = ({ children }) => {
    const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
    const [hasAuthenticated, setHasAuthenticated] = React.useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const authenticateWithFeathers = async () => {
            // Only run once when user becomes authenticated
            if (!isLoading && isAuthenticated && !hasAuthenticated) {
                // ✅ Only run for redirect flow — popup handles its own auth
                const isRedirectFlow = window.location.search.includes('code=')
                    && window.location.search.includes('state=');

                if (!isRedirectFlow) {
                    console.log('⏭️ Auth0RedirectHandler: skipping, not a redirect flow');
                    return;
                }

                setHasAuthenticated(true);

                try {
                    const { feathersClient } = await import('../api/client');

                    // Get Auth0 token
                    const accessToken = await getAccessTokenSilently({
                        authorizationParams: {
                            audience: process.env[`REACT_APP_AUTH0_AUDIENCE_${system.toUpperCase()}`],
                        }
                    });

                    console.log('✅ Got Auth0 token, authenticating with backend...');

                    // Authenticate with Feathers
                    const authenticatedUser = await feathersClient.authenticate({
                        strategy: 'auth0',
                        access_token: accessToken,
                    });

                    console.log('✅ Feathers authentication successful:', authenticatedUser);

                    // Determine role and navigate
                    let role;
                    if (authenticatedUser?.email?.includes('zofim.org.il')) {
                        role = 'tribe';
                        navigate('/tribe');
                    } else {
                        role = 'parent';
                        if (authenticatedUser.hasChildren) {
                            navigate('/parent');
                        } else {
                            navigate('/register/addchild');
                        }
                    }

                } catch (error) {
                    console.error('❌ Feathers authentication failed:', error);
                    // Navigate to login on error
                    navigate('/');
                }
            }
        };
        authenticateWithFeathers();
    }, [isAuthenticated, isLoading, hasAuthenticated, getAccessTokenSilently, navigate]);

    return children;
};